import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, IconButton, MenuItem, Select, Stack, Table, TableBody,
	TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppBulkEditAction, AppPillToggle,
	secondaryButtonSx,
} from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { Add as AddIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon, LinkOutlined as LinkIcon, ArrowRightAlt as ArrowIcon, SearchOutlined as SearchIcon, FilterAltOutlined as FilterIcon } from 'Pages/DsRepricing/@components/icons'

import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { uid } from 'Pages/DsRepricing/Setup/@mocks'
import { useSetupCollection } from 'Pages/DsRepricing/Setup/@services'
import {
	Surface, SurfaceHeader, EmptyState, LoadingState, HelpNote,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx, rowHoverSx, cellPadSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'

// Persistence: @services/useSetupCollection (entity 'tethers').

/**
 * Tethering — inheritance rules between values of a same dimension.
 *
 * When the parent is repriced to X, EACH child is inferred as X · (1+%)
 * or X + € — no independent computation, just a fixed spread. One
 * parent can fan out to multiple children that all share the same
 * delta. What stays strict is that a value can only play ONE role in
 * ONE tether: no chains (child of A can't become parent of B) and no
 * cross-tether reuse. Conflicts impossible by construction.
 *
 * Dimensions supported: LOR, Category (macro), ACRISS (SIPP), Office.
 * Each dimension has its own pool of values so tethers only compare
 * apples to apples.
 */

// v1 → v2 migration: single `child` becomes `children: [child]`.
function migrateTether(t) {
	if (!t) return t
	if (Array.isArray(t.children)) return t
	if (t.child) {
		const { child, ...rest } = t
		return { ...rest, children: [child] }
	}
	return { ...t, children: [] }
}

// Discrete LOR breakpoints seen in real RateGain exports.
const LOR_VALUES = [
	{ code: '1',  label: '1 day'    },
	{ code: '2',  label: '2 days'   },
	{ code: '3',  label: '3 days'   },
	{ code: '4',  label: '4 days'   },
	{ code: '5',  label: '5 days'   },
	{ code: '7',  label: '7 days'   },
	{ code: '10', label: '10 days'  },
	{ code: '14', label: '14 days'  },
	{ code: '21', label: '21 days'  },
	{ code: '28', label: '28 days'  },
	{ code: '30', label: '30+ days' },
]

const DIMENSIONS = [
	{ code: 'lor',      label: 'LOR',      short: 'LOR',      mono: false,
	  palette: { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder } },
	{ code: 'category', label: 'Category', short: 'CATEGORY', mono: false,
	  palette: { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder } },
	{ code: 'acriss',   label: 'ACRISS',   short: 'ACRISS',   mono: true,
	  palette: { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder } },
	{ code: 'office',   label: 'Office',   short: 'OFFICE',   mono: true,
	  palette: { bg: dsColorNew.successBg, fg: dsColorNew.successText, border: dsColorNew.successBar } },
]
const DIM_BY_CODE = Object.fromEntries(DIMENSIONS.map(d => [d.code, d]))

/**
 * The values a tether dimension can take, from the tenant's own data.
 *
 * `ACRISS_TREE`, `ALL_SIPPS` and `OFFICES` were hand-written for the
 * prototype: the offices were six Canarian airports the tenant does
 * not have (its office is `A1`) and the SIPPs missed eight codes the
 * engine is repricing. A tether built from either could never match
 * (Curro, 2026-09-28: "nada mock ya").
 *
 * Both hooks memoise their request at module level, so every component
 * below can ask without costing a round trip.
 */
function useDimensionCatalog() {
	const { tree, allSipps } = useCategories()
	const { offices } = useOfficeScope()
	return useMemo(() => {
		const values = (dim) => {
			switch (dim) {
				case 'lor':      return LOR_VALUES
				case 'category': return tree.map(m => ({ code: m.code, label: m.name }))
				case 'acriss':   return allSipps.map(s => ({ code: s, label: s }))
				case 'office':   return offices.map(o => ({
					code: o.code,
					label: o.name && o.name !== o.code ? `${o.code} · ${o.name}` : o.code,
				}))
				default:         return []
			}
		}
		const label = (dim, code) =>
			values(dim).find(v => v.code === code)?.label || code
		// Compact form for a chip: no "· name" tail.
		const short = (dim, code) => {
			if (dim === 'office') return code
			if (dim === 'category') return label(dim, code)
			return shortLabelForValue(dim, code)
		}
		return { values, label, short }
	}, [tree, allSipps, offices])
}

/** The cases that need no catalogue. Category goes through the hook. */
function shortLabelForValue(dim, code) {
	if (dim === 'lor') return LOR_VALUES.find(v => v.code === code)?.label || `${code} d`
	return code
}

function formatDelta(d) {
	if (!d || d.v == null) return ''
	const sign = d.v > 0 ? '+' : ''
	return d.k === 'pct' ? `${sign}${d.v}%` : `${sign}${d.v}€`
}

function autoName(t, short = shortLabelForValue) {
	const dimLabel = DIM_BY_CODE[t.dimension]?.short || t.dimension
	const p = short(t.dimension, t.parent)
	const children = t.children || []
	if (children.length === 0) return `${dimLabel}: ${p} → …`
	if (children.length === 1) return `${dimLabel}: ${p} → ${short(t.dimension, children[0])}`
	if (children.length <= 3) {
		return `${dimLabel}: ${p} → ${children.map(c => short(t.dimension, c)).join(', ')}`
	}
	return `${dimLabel}: ${p} → ${children.length} children`
}

// A value is "in use" if it's the parent or ANY child of ANY tether.
// Ignore a specific tether id when we're editing it (its own values
// shouldn't exclude themselves).
function inUseValues(tethers, dimension, ignoreId = null) {
	const set = new Set()
	for (const t of tethers) {
		if (t.dimension !== dimension) continue
		if (t.id === ignoreId) continue
		if (t.parent) set.add(t.parent)
		for (const c of t.children || []) set.add(c)
	}
	return set
}

const DEFAULT_TETHERS = [
	{ id: uid(), dimension: 'lor',      parent: '1',       children: ['3', '5'],
	  delta: { k: 'pct', v: 5 },  name_custom: false, name: '' },
	{ id: uid(), dimension: 'lor',      parent: '7',       children: ['14', '21'],
	  delta: { k: 'pct', v: -8 }, name_custom: false, name: '' },
	{ id: uid(), dimension: 'category', parent: 'compact', children: ['standard'],
	  delta: { k: 'abs', v: 8 },  name_custom: false, name: '' },
	{ id: uid(), dimension: 'acriss',   parent: 'MDMR',    children: ['MDAR'],
	  delta: { k: 'pct', v: 12 }, name_custom: true,  name: 'Manual→Auto (Mini)' },
	{ id: uid(), dimension: 'office',   parent: 'LPA-AP',  children: ['LPA-CEN'],
	  delta: { k: 'abs', v: 3 },  name_custom: false, name: '' },
].map(t => ({ ...t, name: t.name_custom ? t.name : autoName(t) }))

export default function TetheringTab() {
	const cat = useDimensionCatalog()
	// Persisted through webback (or the localStorage mock behind the flag).
	const [tethers, setTethers, sync] = useSetupCollection('tethers', {
		mockDefault: DEFAULT_TETHERS, normalise: migrateTether,
	})
	const [dimFilter, setDimFilter] = useState('*')
	const [search, setSearch]       = useState('')
	const [dialogState, setDialogState] = useState(null)  // {mode:'add'|'edit', tether}

	function upsertTether(next) {
		setTethers(prev => {
			const idx = prev.findIndex(t => t.id === next.id)
			if (idx < 0) return [next, ...prev]
			const patched = [...prev]
			patched[idx] = next
			return patched
		})
	}
	function deleteTether(id) {
		setTethers(prev => prev.filter(t => t.id !== id))
	}

	const visible = useMemo(() => tethers.filter(t => {
		if (dimFilter !== '*' && t.dimension !== dimFilter) return false
		if (search) {
			const hay = [t.name, t.parent,
			             ...(t.children || []),
			             cat.label(t.dimension, t.parent),
			             ...(t.children || []).map(c => cat.label(t.dimension, c))]
				.filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(search.toLowerCase())) return false
		}
		return true
	}), [tethers, dimFilter, search])

	return (
		<Surface padded={false}>
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Box>
						<strong>How Strategies use tethers:</strong> when the engine
						reprices a parent value, it looks up any tether where that
						value is a parent and pushes each derived child too — same
						spread for all children of a tether. Tethers save you from
						repricing every LOR / category / office when a fixed spread
						is good enough. A value can only play one role in one tether:
						if <em>X → {'{'}Y,&nbsp;Z{'}'}</em> exists, none of X, Y or Z can
						appear anywhere else.
						<br/><br/>
						<strong>Application order (PRD):</strong> when several
						dimensions have tethers that could kick in for the same
						repricing hit, the engine applies them in a fixed order:
						{' '}<strong>ACRISS → LOR → Office</strong>. Category tethers
						run alongside ACRISS at the same level (macro before individual
						code). This order is not surfaced in the UI — it lives in the
						engine — but is a stable contract between Setup and Repricing.
						</Box>
					</>}
					icon="link"
					title="Tethering (Reglas de herencia)"
					subtitle={<>When a value is repriced, another value inherits the
					result with a <strong>fixed spread</strong> (%, €). Strict 1-to-1:
					a value that already plays parent OR child in a tether can't be
					used elsewhere — no chains, no bifurcations, no conflicts by
					construction.</>}
					actions={
						<AppBulkEditAction startIcon={<AddIcon/>}
						                   onClick={() => setDialogState({ mode: 'add' })}>
							Add tether
						</AppBulkEditAction>
					}
					sx={{ mb: 2 }}
				/>

				{/* The table's filters on the card itself, as the fields of the app (no grey box) */}
				<Box sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
					<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
						<Select size="small" value={dimFilter}
						        onChange={(e) => setDimFilter(e.target.value)}
						        sx={{ minWidth: 180 }}>
							<MenuItem value="*"><em>All dimensions</em></MenuItem>
							{DIMENSIONS.map(d => (
								<MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>
							))}
						</Select>
						<TextField size="small"
						           placeholder="Search by name, value…"
						           value={search}
						           onChange={(e) => setSearch(e.target.value)}
						           InputProps={{ startAdornment: (
						               <SearchIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle, mr: 1 }}/>
						           ) }}
						           sx={{ minWidth: 280 }}/>
						<Box sx={{ flex: 1 }}/>
						<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
							{visible.length === tethers.length
							  ? `${tethers.length} tether${tethers.length === 1 ? '' : 's'}`
							  : `${visible.length} of ${tethers.length} shown`}
						</Typography>
						{(dimFilter !== '*' || search) && (
							<Button variant="outlined" onClick={() => { setDimFilter('*'); setSearch('') }}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Clear
							</Button>
						)}
					</Stack>
				</Box>

				{sync.loading ? (
					<LoadingState what="tethers"/>
				) : visible.length === 0 ? (
					<EmptyState
						icon={<LinkIcon/>}
						title={tethers.length === 0 ? 'No tethers yet' : 'No tethers match these filters'}
						description={tethers.length === 0
						  ? 'Create a first tether to have one value inherit the price of another with a fixed spread.'
						  : 'Loosen the filter or clear it above.'}
						action={
							<Button variant="outlined" startIcon={<AddIcon/>}
							        onClick={() => setDialogState({ mode: 'add' })}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Add tether
							</Button>
						}
					/>
				) : (
					<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder,
					                      borderRadius: 1, overflow: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<HeadCell width={240}>Name</HeadCell>
									<HeadCell width={110}>Dimension</HeadCell>
									<HeadCell>Parent → Child</HeadCell>
									<HeadCell align="right" width={100}>Delta</HeadCell>
									<HeadCell width={90}></HeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(t => (
									<TableRow key={t.id} sx={{ ...rowHoverSx }}>
										<TableCell sx={cellPadSx}>
											<Typography sx={{
												fontSize: 13,
												fontWeight: t.name_custom ? 700 : 500,
												fontStyle: t.name_custom ? 'normal' : 'italic',
												color: t.name_custom ? dsColorNew.textTitle : dsColorNew.textMuted,
											}}>
												{t.name}
											</Typography>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<DimensionChip dim={t.dimension}/>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<TetherArrow tether={t}/>
										</TableCell>
										<TableCell align="right" sx={cellPadSx}>
											<DeltaChip delta={t.delta}/>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<Stack direction="row" gap={0.25} alignItems="center" justifyContent="flex-end">
												<UnsavedChip refused={sync.refused?.get(t.id)}
												             stalled={sync.stalled?.has(t.id)}
												             reason={sync.held?.has(t.id)
												               ? 'Pick at least one value that inherits and it saves itself.' : null}/>
												<IconButton size="small"
												            title="Edit tether"
												            onClick={() => setDialogState({ mode: 'edit', tether: t })}
												            sx={{ color: dsColorNew.textMuted,
												                  '&:hover': { color: dsColorNew.appBlue,
												                               bgcolor: dsColorNew.pillActiveBg } }}>
													<EditIcon fontSize="small"/>
												</IconButton>
												<IconButton size="small"
												            title="Delete tether"
												            onClick={() => deleteTether(t.id)}
												            sx={{ color: dsColorNew.textMuted,
												                  '&:hover': { color: dsColorNew.dangerRed,
												                               bgcolor: dsColorNew.dangerBg } }}>
													<DeleteIcon fontSize="small"/>
												</IconButton>
											</Stack>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

			</Box>

			<TetherDialog state={dialogState}
			              tethers={tethers}
			              onClose={() => setDialogState(null)}
			              onSave={(t) => { upsertTether(t); setDialogState(null) }}/>
		</Surface>
	)
}

// ---------------------------------------------------------------------------
// Dialog — Add / Edit tether
// ---------------------------------------------------------------------------
function TetherDialog({ state, tethers, onClose, onSave }) {
	const cat = useDimensionCatalog()
	const isOpen = !!state
	const isEdit = state?.mode === 'edit'
	const editingId = isEdit ? state.tether.id : null

	const [dimension, setDimension]       = useState('lor')
	const [parentCode, setParentCode]     = useState('')
	const [childCodes, setChildCodes]     = useState([])
	const [delta, setDelta]               = useState({ k: 'pct', v: 5 })
	// Direction is held apart from the amount. `delta.v` stays the signed
	// number that goes on the wire, but a sign derived from it alone is
	// lost the moment the amount is 0 — you would press "−", the field
	// would still read 0, and the toggle would spring back to "+".
	const [down, setDown]                 = useState(false)
	const [name, setName]                 = useState('')
	const [nameCustom, setNameCustom]     = useState(false)

	// Seed / reset when the dialog opens or the target changes.
	useEffect(() => {
		if (!isOpen) return
		if (isEdit) {
			const t = state.tether
			setDimension(t.dimension)
			setParentCode(t.parent)
			setChildCodes(Array.isArray(t.children) ? t.children : (t.child ? [t.child] : []))
			setDelta(t.delta || { k: 'pct', v: 0 })
			setDown((t.delta?.v ?? 0) < 0)
			setName(t.name || '')
			setNameCustom(!!t.name_custom)
		} else {
			setDimension('lor')
			setParentCode('')
			setChildCodes([])
			setDelta({ k: 'pct', v: 5 })
			setDown(false)
			setName('')
			setNameCustom(false)
		}
	}, [isOpen, editingId])

	// The only writer of `delta.v`: whichever half the operator touched,
	// the wire value is rebuilt from both.
	const setSigned = (isDown, magnitude) => {
		setDown(isDown)
		setDelta(d => ({ ...d, v: (isDown ? -1 : 1) * Math.abs(magnitude || 0) }))
	}

	const used = useMemo(() =>
		inUseValues(tethers, dimension, editingId), [tethers, dimension, editingId])

	const allValues = cat.values(dimension)
	const parentOptions = allValues.filter(v => !used.has(v.code)
	                                            && !childCodes.includes(v.code))
	const childOptions  = allValues.filter(v => !used.has(v.code)
	                                            && v.code !== parentCode)

	// Auto-derive name until the user takes ownership.
	useEffect(() => {
		if (nameCustom) return
		if (!parentCode || childCodes.length === 0) { setName(''); return }
		setName(autoName({ dimension, parent: parentCode, children: childCodes }, cat.short))
	}, [dimension, parentCode, childCodes, nameCustom])

	const isValid = parentCode && childCodes.length > 0
	                && !childCodes.includes(parentCode)
	                && !used.has(parentCode)
	                && !childCodes.some(c => used.has(c))
	                && Number.isFinite(delta.v)

	function commit() {
		if (!isValid) return
		const derivedName = autoName({ dimension, parent: parentCode, children: childCodes }, cat.short)
		const t = {
			id: isEdit ? state.tether.id : uid(),
			dimension,
			parent: parentCode,
			children: [...childCodes],
			delta,
			name: nameCustom ? (name.trim() || derivedName) : derivedName,
			name_custom: nameCustom,
		}
		onSave(t)
	}

	if (!isOpen) return null

	// Preview line — grounds the abstract math in a concrete sentence.
	const derivedFormula = delta.k === 'pct'
		? `X × ${(1 + (delta.v || 0) / 100).toFixed(3)}€`
		: `X ${delta.v >= 0 ? '+' : '−'} ${Math.abs(delta.v || 0)}€`
	const childList = childCodes.map(c => cat.short(dimension, c))
	const preview = (parentCode && childCodes.length > 0) ? (
		<>
			When <strong>{cat.short(dimension, parentCode)}</strong> is
			repriced to <strong>X€</strong>,{' '}
			{childList.length === 1 ? (
				<><strong>{childList[0]}</strong>{' '}will be</>
			) : (
				<>
					{childList.slice(0, -1).map((c, i) => (
						<React.Fragment key={i}>
							{i > 0 && ', '}<strong>{c}</strong>
						</React.Fragment>
					))}
					{' '}and{' '}<strong>{childList[childList.length - 1]}</strong>{' '}
					will each be
				</>
			)}
			{' '}pushed to <strong>{derivedFormula}</strong> automatically.
		</>
	) : (
		<em style={{ color: dsColorNew.textMuted }}>
			Pick a parent and at least one child to see the preview.
		</em>
	)

	return (
		<AppDialog open={isOpen} onClose={onClose} maxWidth="sm" paperSx={setupScopeSx}>
			<AppDialogHeader icon="link" title={isEdit ? 'Edit tether' : 'Add tether'} onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Stack gap={2}>
					<Box>
						<Label>Dimension</Label>
						<Select size="small" fullWidth value={dimension}
						        onChange={(e) => {
						            setDimension(e.target.value)
						            setParentCode('')
						            // `setChildCodes([])`, plural: the singular
						            // setter is a leftover from when a tether had
						            // one child, and changing the dimension threw
						            // `ReferenceError` instead of clearing it.
						            setChildCodes([])
						        }}
						        sx={{ bgcolor: dsColorNew.white, ...inputSx }}>
							{DIMENSIONS.map(d => (
								<MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>
							))}
						</Select>
					</Box>

					<Stack direction="row" gap={1.5} alignItems="flex-end">
						<Box sx={{ flex: 1 }}>
							<Label>Parent</Label>
							<ValuePicker options={parentOptions}
							             value={parentCode}
							             includeInEditModeSelf={isEdit ? state.tether.parent : null}
							             allValues={allValues}
							             dim={dimension}
							             onChange={(v) => setParentCode(v)}
							             placeholder="Select parent"/>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', height: 40, pb: 0.5,
						           color: dsColorNew.textMuted }}>
							<ArrowIcon/>
						</Box>
						<Box sx={{ flex: 1.4 }}>
							<Label>Children (one or more, same delta)</Label>
							<ChildrenPicker options={childOptions}
							                value={childCodes}
							                includeInEditModeSelf={isEdit ? (state.tether.children || (state.tether.child ? [state.tether.child] : [])) : []}
							                allValues={allValues}
							                dim={dimension}
							                onChange={setChildCodes}
							                placeholder="Add one or more children…"/>
						</Box>
					</Stack>

					<Box>
						<Label>Delta</Label>
						<Stack direction="row" gap={0.75} alignItems="center">
							<DirectionToggle value={down ? 'down' : 'up'}
							                 onChange={(d) => setSigned(d === 'down', delta.v)}/>
							<TextField type="number" size="small"
							           value={delta.v == null ? '' : Math.abs(delta.v)}
							           onChange={(e) => {
							               const raw = e.target.value
							               if (raw === '') return setSigned(down, 0)
							               const n = Number(raw)
							               if (!Number.isFinite(n)) return
							               // A pasted "-8" sets the direction instead of
							               // being thrown away.
							               setSigned(n < 0 ? true : down, n)
							           }}
							           onKeyDown={(e) => {
							               // The reason a negative delta could not be typed:
							               // a number input reports a lone "-" as an empty
							               // string, and the old handler read that as 0, so
							               // the key did nothing at all. Now it does the
							               // thing it looks like it does.
							               if (e.ctrlKey || e.metaKey || e.altKey) return
							               if (e.key !== '-' && e.key !== '+') return
							               e.preventDefault()
							               setSigned(e.key === '-', delta.v)
							           }}
							           inputProps={{ min: 0,
							                         style: { textAlign: 'right', fontWeight: 700,
							                                  fontSize: 14, width: 80 } }}
							           sx={{ bgcolor: dsColorNew.white, ...inputSx,
							                 '& .MuiOutlinedInput-root': { height: 40 } }}/>
							<UnitToggle value={delta.k}
							            onChange={(k) => setDelta({ ...delta, k })}/>
							<Chip label={formatDelta(delta) || '—'} size="small"
							      sx={{ height: 32, fontSize: 13, fontWeight: 700,
							            minWidth: 70, borderRadius: 1,
							            bgcolor: delta.v > 0 ? dsColorNew.successBg
							                   : delta.v < 0 ? dsColorNew.dangerBg : dsColorNew.pillTrackBg,
							            color:   delta.v > 0 ? dsColorNew.successText
							                   : delta.v < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
							            border: '1px solid',
							            borderColor: delta.v > 0 ? alpha(dsColorNew.successBar, 0.4)
							                       : delta.v < 0 ? dsColorNew.dangerBorder : dsColorNew.grayBorder }}/>
						</Stack>
					</Box>

					<Box>
						<Label>Name</Label>
						<Stack direction="row" gap={0.75}>
							<TextField size="small" fullWidth
							           value={name}
							           onChange={(e) => { setName(e.target.value); setNameCustom(true) }}
							           inputProps={{ style: { fontWeight: nameCustom ? 700 : 500,
							                                  fontStyle: nameCustom ? 'normal' : 'italic' } }}
							           sx={{ bgcolor: dsColorNew.white, ...inputSx }}/>
							{nameCustom && (
								<Button size="small" variant="text"
								        onClick={() => setNameCustom(false)}
								        sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
									Reset to auto
								</Button>
							)}
						</Stack>
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
							Auto-derived from parent → child. Edit to override.
						</Typography>
					</Box>

					<Box sx={{ p: 1.25, bgcolor: dsColorNew.noteInfoBg, border: `1px solid ${dsColorNew.noteInfoBorder}`,
					           borderRadius: 1 }}>
						<Typography sx={{ fontSize: 11, fontWeight: 700,
						                  textTransform: 'uppercase', letterSpacing: '0.06em',
						                  color: dsColorNew.appBlue, mb: 0.5 }}>
							Preview
						</Typography>
						<Typography sx={{ fontSize: 13, color: dsColorNew.textTitle,
						                  lineHeight: 1.5 }}>
							{preview}
						</Typography>
					</Box>

					{parentOptions.length === 0 && childOptions.length === 0 && (
						<Typography sx={{ fontSize: 12.5, color: dsColorNew.dangerRed }}>
							No values left in <strong>{DIM_BY_CODE[dimension]?.label}</strong>
							{' '}— every one is already used in another tether.
						</Typography>
					)}
				</Stack>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction disabled={!isValid} onClick={commit}>
					{isEdit ? 'Save changes' : 'Save tether'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}

function ChildrenPicker({ options, value, includeInEditModeSelf, allValues, dim, onChange, placeholder }) {
	const cat = useDimensionCatalog()
	const withSelf = useMemo(() => {
		const selfList = Array.isArray(includeInEditModeSelf) ? includeInEditModeSelf : []
		if (!selfList.length) return options
		const known = new Set(options.map(o => o.code))
		const extras = selfList
			.filter(code => !known.has(code))
			.map(code => allValues.find(o => o.code === code))
			.filter(Boolean)
		return [...extras, ...options]
	}, [options, includeInEditModeSelf, allValues])
	const selected = withSelf.filter(o => value.includes(o.code))
	const dimMeta = DIM_BY_CODE[dim]
	return (
		<Autocomplete multiple size="small"
		              options={withSelf}
		              value={selected}
		              disableCloseOnSelect
		              getOptionLabel={(o) => o.label}
		              isOptionEqualToValue={(a, b) => a.code === b.code}
		              onChange={(_, next) => onChange(next.map(o => o.code))}
		              renderTags={(list, getTagProps) =>
		                list.map((o, idx) => (
		                  <Chip {...getTagProps({ index: idx })}
		                        key={o.code} size="small"
		                        label={cat.short(dim, o.code)}
		                        sx={{
		                            height: 22, fontWeight: 700, fontSize: 11.5,
		                            bgcolor: dimMeta?.palette.bg || dsColorNew.pillTrackBg,
		                            color:   dimMeta?.palette.fg || dsColorNew.textTitle,
		                            border: '1px solid',
		                            borderColor: dimMeta?.palette.border || dsColorNew.grayBorder,
		                            fontFamily: dimMeta?.mono ? 'inherit' : 'inherit',
		                        }}/>
		                ))}
		              renderInput={(params) => (
		                  <TextField {...params}
		                             placeholder={value.length ? '' : placeholder}
		                             sx={{ bgcolor: dsColorNew.white, ...inputSx }}/>
		              )}
		              renderOption={(props, o) => (
		                  <li {...props} key={o.code}
		                      style={{ ...props.style,
		                               fontFamily: dimMeta?.mono ? 'inherit' : 'inherit',
		                               fontWeight: 600 }}>
		                      {o.label}
		                  </li>
		              )}/>
	)
}

function ValuePicker({ options, value, includeInEditModeSelf, allValues, dim, onChange, placeholder }) {
	// In edit mode the current value must remain selectable even though
	// it's technically "used".
	const withSelf = useMemo(() => {
		if (!includeInEditModeSelf) return options
		if (options.some(o => o.code === includeInEditModeSelf)) return options
		const self = allValues.find(o => o.code === includeInEditModeSelf)
		return self ? [self, ...options] : options
	}, [options, includeInEditModeSelf, allValues])
	const selected = withSelf.find(o => o.code === value) || null
	return (
		<Autocomplete size="small"
		              options={withSelf}
		              value={selected}
		              getOptionLabel={(o) => o.label}
		              isOptionEqualToValue={(a, b) => a.code === b.code}
		              onChange={(_, next) => onChange(next?.code ?? '')}
		              renderInput={(params) => (
		                  <TextField {...params} placeholder={placeholder}
		                             sx={{ bgcolor: dsColorNew.white, ...inputSx,
		                                   '& .MuiOutlinedInput-input': {
		                                       fontFamily: DIM_BY_CODE[dim]?.mono ? 'inherit' : 'inherit',
		                                       fontWeight: 700,
		                                   } }}/>
		              )}
		              renderOption={(props, o) => (
		                  <li {...props} key={o.code}
		                      style={{ ...props.style,
		                               fontFamily: DIM_BY_CODE[dim]?.mono ? 'inherit' : 'inherit',
		                               fontWeight: 600 }}>
		                      {o.label}
		                  </li>
		              )}/>
	)
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function DimensionChip({ dim }) {
	const d = DIM_BY_CODE[dim]
	if (!d) return <span>{dim}</span>
	return (
		<Chip size="small" label={d.short}
		      sx={{ height: 20, fontSize: 11, fontWeight: 700,
		            letterSpacing: '0.05em',
		            bgcolor: d.palette.bg, color: d.palette.fg,
		            border: '1px solid', borderColor: d.palette.border }}/>
	)
}

function TetherArrow({ tether }) {
	const cat = useDimensionCatalog()
	const d = DIM_BY_CODE[tether.dimension]
	const chipSx = {
		height: 26, fontSize: 12, fontWeight: 700,
		bgcolor: dsColorNew.white,
		border: '1px solid',
		borderColor: d?.palette.border || dsColorNew.grayBorder,
		color: d?.palette.fg || dsColorNew.textTitle,
		fontFamily: d?.mono ? 'inherit' : 'inherit',
		'& .MuiChip-label': { px: 1 },
	}
	const children = tether.children || []
	return (
		<Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
			<Chip size="small" label={cat.short(tether.dimension, tether.parent)} sx={chipSx}/>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 8 }}/>
				<Box sx={{ fontSize: 11, fontWeight: 700,
				           bgcolor: dsColorNew.pillTrackBg,
				           border: '1px solid', borderColor: dsColorNew.grayBorder,
				           borderRadius: 0.75, px: 0.75, py: 0.15,
				           color: tether.delta.v > 0 ? dsColorNew.successText
				                 : tether.delta.v < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
				           fontFamily: 'inherit' }}>
					{formatDelta(tether.delta)}
				</Box>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 8 }}/>
				<ArrowIcon sx={{ fontSize: 20, color: dsColorNew.textSubtle }}/>
			</Box>
			{children.length === 0 ? (
				<Chip size="small" label="—"
				      sx={{ ...chipSx, borderStyle: 'dashed', color: dsColorNew.textSubtle }}/>
			) : (
				<Stack direction="row" gap={0.35} flexWrap="wrap">
					{children.map(c => (
						<Chip key={c} size="small"
						      label={cat.short(tether.dimension, c)}
						      sx={chipSx}/>
					))}
				</Stack>
			)}
		</Stack>
	)
}

function DeltaChip({ delta }) {
	if (!delta || delta.v == null) {
		return <Typography sx={{ color: dsColorNew.textSubtle }}>—</Typography>
	}
	const isZero = delta.v === 0
	const up     = delta.v > 0
	return (
		<Chip size="small" label={formatDelta(delta)}
		      sx={{ height: 24, fontSize: 12.5, fontWeight: 700,
		            fontFamily: 'inherit',
		            bgcolor: isZero ? dsColorNew.pillTrackBg
		                   : up     ? dsColorNew.successBg : dsColorNew.dangerBg,
		            color:   isZero ? dsColorNew.textMuted
		                   : up     ? dsColorNew.successText : dsColorNew.dangerRed,
		            border: '1px solid',
		            borderColor: isZero ? dsColorNew.grayBorder
		                       : up     ? alpha(dsColorNew.successBar, 0.4) : dsColorNew.dangerBorder }}/>
	)
}

/**
 * Up or down, as a control instead of a character the operator has to
 * smuggle past a number input. The app's pill toggle; the delta chip
 * beside it still says dearer in green and cheaper in red.
 */
// Each sign keeps its sentence on hover and for screen readers, as the old buttons did
const DIRECTION_OPTIONS = [
	{ value: 'up', label: <span title="Child is dearer than the parent" aria-label="Child is dearer than the parent">+</span> },
	{ value: 'down', label: <span title="Child is cheaper than the parent" aria-label="Child is cheaper than the parent">−</span> },
]

function DirectionToggle({ value, onChange }) {
	return <AppPillToggle size="small" value={value} options={DIRECTION_OPTIONS} onChange={onChange}/>
}

const UNIT_OPTIONS = [{ value: 'pct', label: '%' }, { value: 'abs', label: '€' }]

function UnitToggle({ value, onChange }) {
	return <AppPillToggle size="small" value={value} options={UNIT_OPTIONS} onChange={onChange}/>
}

function Label({ children }) {
	return (
		<Typography sx={{ fontSize: 11, fontWeight: 700,
		                  textTransform: 'uppercase', letterSpacing: '0.06em',
		                  color: dsColorNew.textMuted, mb: 0.5 }}>
			{children}
		</Typography>
	)
}

function HeadCell({ children, ...rest }) {
	return (
		<TableCell {...rest}
		           sx={{ ...headCellSx, py: 1.25 }}>
			{children}
		</TableCell>
	)
}
